import { useCallback, useEffect, useRef } from 'preact/hooks';
import { route, useRouter } from 'preact-router';
import { useDispatch } from 'react-redux';

import { AppState, useSelector } from '@/redux/helper';
import persistor from '@/redux/persistor';
import { selectHasGuidedExperienceFeature } from '@/redux/selectors/users';
import {
  goBackTo,
  goToStep,
  pauseTour,
  restartTour,
  resumeTour,
  setFurthestStep,
  startTour,
} from '@/redux/slices/guidedTour/actions';
import { trackMP } from '@/shared/mp';

import { getStep, NEXT_HABITAT_SKIP_MAP, TOUR_IS_DEV, TOUR_STEPS, TourButton, TourStep } from './tourSteps';

const selectGuidedTour = (state: AppState) => state.guidedTour;

// Module-level guards survive route remounts while the app session is active.

// True while a tour-triggered navigation is in flight.
let isTourNavigating = false;

// True only when the user explicitly pauses via 'Take a Break'.
let isUserPaused = false;

const useGuidedTour = () => {
  const dispatch = useDispatch();
  const [{ url, matches }] = useRouter();
  const { isActive, isPaused, stepHistory, isCompleted } = useSelector(selectGuidedTour) ?? {};

  const username = useSelector((state) => state.user.username);
  const isLoggedIn = useSelector((state) => state.user.logged);
  const hasActiveL1Plan = useSelector(selectHasGuidedExperienceFeature);
  const furthestStepIndex = useSelector((state) => state.guidedTour.furthestStepIndex);
  const furthestStepId = useSelector((state) => state.guidedTour.furthestStepId);

  const currentStepId: string | undefined = stepHistory?.[stepHistory.length - 1];
  const currentStep: TourStep | undefined = currentStepId ? getStep(currentStepId) : undefined;
  // Track previous id/mediaId to detect modal close for ON_CLOSE steps.
  const prevMediaIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!isLoggedIn) return;
    if (!hasActiveL1Plan) return;
    if (isCompleted) return;
    if (isActive) return;
    if (stepHistory?.length > 0) return;

    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    if (pathname === '/home') {
      trackMP('guided-tour', { tourName: 'All Access Guided Tour', action: 'triggered', furthestStepIndex: -1 });
      dispatch(startTour());
    }
  }, [isLoggedIn, hasActiveL1Plan, isCompleted, isActive, stepHistory, url, dispatch]);
  useEffect(() => {
    if (!isActive) return;
    if (!currentStep || currentStep.componentState === 'modal' || !currentStep.navigationPath) return;

    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const expectedPath = TOUR_IS_DEV
      ? currentStep.devNavigationPath ?? currentStep.navigationPath
      : currentStep.navigationPath;
    const expectedPathname = expectedPath.split('?')[0];

    if (pathname === expectedPathname) {
      if (isTourNavigating) isTourNavigating = false;
      if (isPaused && !isUserPaused) dispatch(resumeTour());
      return;
    }
    if (isTourNavigating) return;

    if (!isPaused) dispatch(pauseTour());
  }, [url, currentStep, isActive, isPaused, dispatch]);
  useEffect(() => {
    // Habitat pages use id; home modal URLs use mediaId.
    const currentMediaId = (matches?.id ?? matches?.mediaId) as string | undefined;

    if (currentStep?.trigger === 'ON_CLOSE') {
      if (prevMediaIdRef.current && !currentMediaId) {
        const nextId = currentStep.nextStepId;
        if (nextId) {
          dispatch(goToStep(nextId));
        }
      }
    }

    prevMediaIdRef.current = currentMediaId;
  }, [matches?.id, matches?.mediaId, currentStep, dispatch]);
  useEffect(() => {
    if (!isActive || isPaused) return undefined;
    if (currentStep?.trigger !== 'DELAY') return undefined;
    if (!currentStep.delayMs || !currentStep.nextStepId) return undefined;

    const timer = setTimeout(() => {
      dispatch(goToStep(currentStep.nextStepId!));
    }, currentStep.delayMs);

    return () => clearTimeout(timer);
  }, [currentStepId, isActive, isPaused, currentStep, dispatch]);
  useEffect(() => {
    if (!isActive || !currentStepId) return;

    const stepIndex = TOUR_STEPS.findIndex((s) => s.id === currentStepId);
    const step = getStep(currentStepId);
    const newFurthestIndex = Math.max(furthestStepIndex, stepIndex);
    const newFurthestStepId = stepIndex > furthestStepIndex ? currentStepId : furthestStepId;

    if (stepIndex > furthestStepIndex) {
      dispatch(setFurthestStep({ stepIndex, stepId: currentStepId }));
    }

    trackMP('guided-tour', {
      tourName: 'All Access Guided Tour',
      action: 'step-entered',
      stepId: currentStepId,
      stepIndex,
      furthestStepIndex: newFurthestIndex,
      furthestStepId: newFurthestStepId,
      ...(step?.pausedCurrentSection && { section: step.pausedCurrentSection }),
      ...(step?.pausedNextHabitat && { nextHabitat: step.pausedNextHabitat }),
      ...(step?.componentState && { componentState: step.componentState }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepId, isActive, dispatch]);

  const trackTour = useCallback(
    (action: string, extra?: Record<string, unknown>) => {
      trackMP('guided-tour', {
        tourName: 'All Access Guided Tour',
        action,
        furthestStepIndex,
        furthestStepId,
        ...extra,
      });
    },
    [furthestStepIndex, furthestStepId],
  );

  const resolvePath = useCallback(
    (button: TourButton): string | undefined => {
      let path = TOUR_IS_DEV ? button.devNavigatePath ?? button.navigatePath : button.navigatePath;

      if (path && button.isDynamicUsername && username) {
        path = path.replace('[username]', username);
      }

      return path;
    },
    [username],
  );

  const handleButtonClick = useCallback(
    (button: TourButton) => {
      if (button.restartTour) {
        trackTour('restarted', { stepId: currentStepId });
        isTourNavigating = true;
        dispatch(restartTour());
        route('/home');
        return;
      }

      const path = resolvePath(button);

      if (path) {
        if (button.forceReload) {
          const targetId = button.targetStepId ?? currentStep?.nextStepId;
          if (targetId) dispatch(goToStep(targetId));
          trackTour('button-clicked', {
            stepId: currentStepId,
            buttonLabel: button.label,
            ...(currentStep?.pausedCurrentSection && { section: currentStep.pausedCurrentSection }),
            navigatesTo: path,
          });
          // Flush redux-persist writes before hard navigation so tour state survives the reload.
          // Navigate in finally() so a flush failure doesn't leave the user stuck.
          persistor.flush().finally(() => {
            window.location.href = path;
          });
          return;
        }
        isTourNavigating = true;
        route(path);
      }

      trackTour('button-clicked', {
        stepId: currentStepId,
        buttonLabel: button.label,
        ...(currentStep?.pausedCurrentSection && { section: currentStep.pausedCurrentSection }),
        ...(path && { navigatesTo: path }),
      });
      const targetId = button.targetStepId ?? currentStep?.nextStepId;
      if (targetId) {
        dispatch(goToStep(targetId));
      }
    },
    [resolvePath, currentStep, currentStepId, dispatch, trackTour],
  );

  const handleGoBack = useCallback(() => {
    if (!stepHistory || stepHistory.length <= 1) return;
    let targetStepId: string | undefined;
    // Skip ON_CLOSE steps when walking back; those are transient video-open states.
    for (let i = stepHistory.length - 2; i >= 0; i -= 1) {
      const candidateId = stepHistory[i];
      if (candidateId === '0-modal') break;
      const candidate = getStep(candidateId);
      if (candidate?.trigger !== 'ON_CLOSE') {
        targetStepId = candidateId;
        break;
      }
    }

    if (!targetStepId) return;

    trackTour('went-back', {
      fromStepId: currentStepId,
      fromSection: currentStep?.pausedCurrentSection,
      toStepId: targetStepId,
    });
    const targetStep = getStep(targetStepId);
    const navPath = targetStep?.navigationPath;
    if (navPath) {
      const path = TOUR_IS_DEV ? targetStep.devNavigationPath ?? navPath : navPath;
      isTourNavigating = true;
      route(path);
    }
    dispatch(goBackTo(targetStepId));
  }, [stepHistory, currentStepId, currentStep, dispatch, trackTour]);

  const handlePause = useCallback(() => {
    isUserPaused = true;
    trackTour('paused', {
      stepId: currentStepId,
      ...(currentStep?.pausedCurrentSection && { section: currentStep.pausedCurrentSection }),
      ...(currentStep?.pausedNextHabitat && { nextHabitat: currentStep.pausedNextHabitat }),
    });
    dispatch(pauseTour());
  }, [dispatch, currentStep, currentStepId, trackTour]);

  const handleSkipAhead = useCallback(() => {
    const nextHabitat = currentStep?.pausedNextHabitat;
    if (!nextHabitat) return;

    const entry = NEXT_HABITAT_SKIP_MAP[nextHabitat];
    if (!entry) return;

    const path = TOUR_IS_DEV ? entry.devNavigatePath ?? entry.navigatePath : entry.navigatePath;

    trackTour('skipped-ahead', {
      fromStepId: currentStepId,
      fromSection: currentStep?.pausedCurrentSection,
      toStepId: entry.stepId,
      toHabitat: nextHabitat,
    });

    isUserPaused = false;
    isTourNavigating = true;
    dispatch(resumeTour());
    dispatch(goToStep(entry.stepId));
    route(path);
  }, [currentStep, currentStepId, dispatch, trackTour]);

  const handleResume = useCallback(() => {
    trackTour('resumed', {
      stepId: currentStepId,
      ...(currentStep?.pausedCurrentSection && { section: currentStep.pausedCurrentSection }),
      ...(currentStep?.pausedNextHabitat && { nextHabitat: currentStep.pausedNextHabitat }),
    });
    // If paused on an idle step, resume from the nearest prior conversational step.
    if (currentStep?.componentState === 'idle' && stepHistory && stepHistory.length > 1) {
      let targetStepId: string | undefined;
      for (let i = stepHistory.length - 2; i >= 0; i -= 1) {
        const candidateId = stepHistory[i];
        if (candidateId === '0-modal') break;
        const candidate = getStep(candidateId);
        if (candidate?.trigger !== 'ON_CLOSE') {
          targetStepId = candidateId;
          break;
        }
      }

      if (targetStepId) {
        const targetStep = getStep(targetStepId);
        const navPath = targetStep?.navigationPath;
        if (navPath) {
          const path = TOUR_IS_DEV ? targetStep.devNavigationPath ?? navPath : navPath;
          isTourNavigating = true;
          route(path);
        }
        isUserPaused = false;
        dispatch(resumeTour());
        dispatch(goBackTo(targetStepId));
        return;
      }
    }

    if (currentStep?.navigationPath && currentStep.componentState !== 'modal') {
      const expectedPath = TOUR_IS_DEV
        ? currentStep.devNavigationPath ?? currentStep.navigationPath
        : currentStep.navigationPath;
      const expectedPathname = expectedPath.split('?')[0];
      const currentPathname = typeof window !== 'undefined' ? window.location.pathname : '';
      if (currentPathname !== expectedPathname) {
        isTourNavigating = true;
        route(expectedPath);
      }
    }

    isUserPaused = false;
    dispatch(resumeTour());
  }, [dispatch, currentStep, currentStepId, stepHistory, trackTour]);

  return {
    isActive,
    isPaused,
    isCompleted,
    currentStep,
    currentStepId,
    stepHistory,
    handleButtonClick,
    handleGoBack,
    handlePause,
    handleResume,
    handleSkipAhead,
    resolvePath,
  };
};

export default useGuidedTour;
